// The corner window's hunt scene: the same map, sprites and seals as the hunt view (client/field.ts), drawn for a
// window the player glances at while working. It follows the lead like the hunt view does, but stays quiet: no name
// tags but an Aura's, health bars only on the fight itself, the coins a kill pays instead of every blow's damage,
// and one small impact per blow. Big moments (an Aura, a catch) still read at a glance.
//
// It draws into a canvas in the corner window's own document and runs on that window's animation frames, so it
// keeps moving while the game's tab is hidden behind other windows.
import { CONFIG } from '../../shared/config.ts';
import { MOVE, WAVE_RADIUS, type AnimKind } from '../../shared/moves.ts';
import type { Element } from '../../shared/elements.ts';
import { mapFor, type WorldMap } from '../../shared/worldmap.ts';
import type { GameEvent, WildDTO } from '../../shared/protocol.ts';
import { paintTerrain } from '../terrain.ts';
import { paintDecor, dmgText } from '../field.ts';
import { sprite, spriteSize, has, sheet, drawable, silhouette, sheetInfo, sheetCell, artInfo } from '../assets.ts';
import { EL_RAMP, particleSprite, sparkSprite, stamp } from '../hitfx.ts';
import { S, serverNow } from '../state.ts';
import { myArt } from '../skins.ts';
import { heading, steer, holdDir, holdFace, type Dir, type Heading } from '../facing.ts';
import { TILE, sceneScale, type Shape } from './model.ts';
import { mySprite } from '../trainerart.ts';

type Facing = 'front' | 'left' | 'back' | 'right';
const ROW: Record<Facing, number> = { front: 0, left: 1, back: 2, right: 3 };
const FACING: Record<Dir, Facing> = { down: 'front', up: 'back', left: 'left', right: 'right' };
/** As the hunt view: sheet cells drawn at 0.75 map px per sheet px, and the same frame timings. */
const SHEET_SCALE = 0.75;
const ATK_MS = 70, WALK_MS = 140, IDLE_MS = 150, FLASH_MS = 110;
const dirTo = (dx: number, dy: number): Dir => (Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up');
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

interface Anim { face: Facing; x: number; y: number; moved: number; atk: number; hit: number; seed: number; seen: number; head: Heading }

type Fx =
  | { k: 'text'; x: number; y: number; text: string; color: string; t0: number; px: number }
  | { k: 'impact'; kind: AnimKind; el: Element; x: number; y: number; fx: number; fy: number; t0: number; seed: number; awk: boolean }
  | { k: 'throw'; uid: number; fx: number; fy: number; t0: number }
  | { k: 'burst'; x: number; y: number; color: string; t0: number; n: number };

/** How long each impact runs: a small hit is brief; a Wave or a Signature lingers a little. */
const IMPACT_MS: Record<AnimKind, number> = { jab: 300, slash: 340, projectile: 460, beam: 460, burst: 520, wave: 760, signature: 980 };
const FLIGHT_MS = 180;
const GILT = ['#6a3810', '#e5a632', '#ffd468', '#fff3c0'];

/** CSS pixels of the window that the HUD covers on each side; the camera centres the fight in the rest. */
export interface Insets { l: number; r: number; t: number; b: number }

export class CornerScene {
  private g: CanvasRenderingContext2D;
  private dpr = 1;
  private K = 2;
  private cam = { x: 0, y: 0, ok: false };
  private terrain: { key: string; c: HTMLCanvasElement; decor: boolean } | null = null;
  private anims = new Map<string, Anim>();
  private fx: Fx[] = [];
  private trail: { x: number; y: number }[] = [];
  private trainer = { x: 0, y: 0, dir: 'down' as Dir, moving: false, throwAt: -1e9, head: heading() };
  private trainerAnim: Anim = { face: 'front', x: 0, y: 0, moved: 0, atk: -1e9, hit: -1e9, seed: 0, seen: 0, head: heading() };
  private ground = '';
  private lastT = 0;
  private pruned = 0;
  private leadFace = true;
  private lastLeadX = 0;
  private tops = new Map<string, number>();
  /** Set by the window: layout, HUD cover, zoom step, reduced motion. */
  shape: Shape = 'card';
  insets: Insets = { l: 0, r: 0, t: 0, b: 0 };
  zoom = 0;
  quiet = false;

  private canvas: HTMLCanvasElement;
  private win: Window;

  constructor(canvas: HTMLCanvasElement, win: Window) {
    this.canvas = canvas; this.win = win;
    this.g = canvas.getContext('2d')!;
  }

  /** Match the canvas to its box. Call on every window resize. */
  fit() {
    this.dpr = Math.min(2, this.win.devicePixelRatio || 1);
    const r = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(r.width * this.dpr)), h = Math.max(1, Math.round(r.height * this.dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) { this.canvas.width = w; this.canvas.height = h; }
  }

  /** A new hunt or a reconnect: re-centre and let the trainer start fresh beside the lead. */
  reset() {
    this.cam.ok = false;
    this.trail.length = 0;
    this.trainer = { x: 0, y: 0, dir: 'down', moving: false, throwAt: -1e9, head: heading() };
    this.fx.length = 0;
    this.anims.clear();
  }

  /** Effects queued but not yet finished (tests and screenshots). */
  get effects() { return this.fx.length; }

  // ---------------- coordinates ----------------
  private sx(x: number) { return Math.round((x * TILE - this.cam.x) * this.K); }
  private sy(y: number) { return Math.round((y * TILE - this.cam.y) * this.K); }

  private wildPos(w: WildDTO, alpha: number) {
    const p = S.prevField?.wilds.find((x) => x.uid === w.uid);
    return p ? { x: lerp(p.x, w.x, alpha), y: lerp(p.y, w.y, alpha) } : { x: w.x, y: w.y };
  }
  private leadPos(alpha: number) {
    const f = S.field;
    if (!f?.lead) return null;
    const p = S.prevField?.lead;
    return p && p.id === f.lead.id ? { x: lerp(p.x, f.lead.x, alpha), y: lerp(p.y, f.lead.y, alpha) } : { x: f.lead.x, y: f.lead.y };
  }
  private posOf(uid: number) {
    const w = S.field?.wilds.find((x) => x.uid === uid) ?? S.prevField?.wilds.find((x) => x.uid === uid);
    return w ? { x: w.x, y: w.y } : null;
  }

  private terrainFor(ground: string): { m: WorldMap; c: HTMLCanvasElement } {
    const m = mapFor(ground);
    if (!this.terrain || this.terrain.key !== m.key) this.terrain = { key: m.key, c: paintTerrain(m), decor: false };
    if (!this.terrain.decor) this.terrain.decor = paintDecor(m, this.terrain.c);
    return { m, c: this.terrain.c };
  }

  // ---------------- sprites ----------------
  private animFor(key: string, x: number, y: number, now: number): Anim {
    let a = this.anims.get(key);
    if (!a) { a = { face: 'front', x, y, moved: 0, atk: -1e9, hit: -1e9, seed: Math.random() * 5000, seen: now, head: heading() }; this.anims.set(key, a); }
    const dx = x - a.x, dy = y - a.y, dt = now - a.seen;
    a.seen = now;
    if (Math.hypot(dx, dy) > 0.004) { a.moved = now; steer(a.head, dx, dy, dt); a.face = holdFace(a.face, a.head.vx, a.head.vy); }
    a.x = x; a.y = y;
    return a;
  }

  private ready(img: CanvasImageSource) { return drawable(img); }

  /** Draw one frame of an animated sheet (rows: facings; columns: walk, idle, attack) standing at (x, y). */
  private drawSheet(id: string, aura: boolean, x: number, y: number, a: Anim, t: number, o: { alpha?: number; squash?: number; face?: Facing }) {
    const info = sheetInfo(id)!;
    const img = sheet(id, aura);
    if (!this.ready(img)) return;
    const g = this.g, n = info.size, p = sheetCell(info), now = performance.now(), K = this.K;
    let col: number;
    if (now - a.atk < info.atk * ATK_MS) col = info.walk + info.idle + Math.floor((now - a.atk) / ATK_MS);
    else if (now - a.moved < 160) col = Math.floor(t / WALK_MS) % info.walk;
    else col = info.walk + (Math.floor((t + a.seed) / IDLE_MS) % info.idle);
    const x0 = col * p, y0 = ROW[o.face ?? a.face] * p;
    const flash = now - a.hit > 0 && now - a.hit < FLASH_MS;
    const w = n * SHEET_SCALE, squash = o.squash ?? 1;
    const foot = this.sy(y + 0.12) + 7 * SHEET_SCALE * K;
    g.globalAlpha = o.alpha ?? 1;
    const dx = Math.round(this.sx(x) - (w / 2) * K), dy = Math.round(foot - w * squash * K), dw = Math.round(w * K), dh = Math.round(w * squash * K);
    g.drawImage(img, x0, y0, p, p, dx, dy, dw, dh);
    // The hit flash: the frame in white over itself.
    if (flash) { g.globalAlpha = (o.alpha ?? 1) * 0.85; g.drawImage(silhouette(img, x0, y0, p, '#fff'), 0, 0, p, p, dx, dy, dw, dh); }
    g.globalAlpha = 1;
  }

  /** Map px from the feet to the top of a creature's head, for the health bar and an Aura's tag. */
  private height(id: string): number {
    const info = sheetInfo(id);
    if (!info) return spriteSize(id) * 0.85;
    const img = sheet(id);
    if (!this.ready(img)) return info.size * SHEET_SCALE * 0.7;
    let top = this.tops.get(id);
    if (top === undefined) {
      const p = sheetCell(info);
      const c = document.createElement('canvas'); c.width = c.height = p;
      const x = c.getContext('2d', { willReadFrequently: true })!;
      x.drawImage(img, info.walk * p, 0, p, p, 0, 0, p, p);
      const d = x.getImageData(0, 0, p, p).data;
      top = 0;
      find: for (let yy = 0; yy < p; yy++) for (let i = 0; i < p; i++) if (d[(yy * p + i) * 4 + 3] === 255) { top = yy; break find; }
      top = top * info.size / p;
      this.tops.set(id, top);
    }
    return (info.size - 7 - top) * SHEET_SCALE;
  }

  private shadow(x: number, y: number, w: number, alpha = 0.22) {
    const g = this.g, K = this.K;
    g.globalAlpha = alpha; g.fillStyle = '#000';
    g.beginPath(); g.ellipse(this.sx(x), this.sy(y), w * 0.32 * K, w * 0.09 * K, 0, 0, Math.PI * 2); g.fill();
    g.globalAlpha = 1;
  }

  private drawImg(img: CanvasImageSource, x: number, foot: number, w: number, h: number, flip = false, alpha = 1, squash = 1) {
    const g = this.g, K = this.K;
    g.globalAlpha = alpha;
    g.save(); g.translate(this.sx(x), this.sy(foot)); if (flip) g.scale(-1, 1);
    g.drawImage(img, Math.round((-w / 2) * K), Math.round(-h * squash * K), Math.round(w * K), Math.round(h * squash * K));
    g.restore(); g.globalAlpha = 1;
  }

  private drawCreature(id: string, aura: boolean, x: number, y: number, faceRight: boolean, o: { alpha?: number; squash?: number; bob?: number; key: string; face?: Facing; t: number }) {
    if (sheetInfo(id)) { this.drawSheet(id, aura, x, y, this.animFor(o.key, x, y, performance.now()), o.t, o); return; }
    const img = sprite(id, aura);
    if (!this.ready(img)) return;
    const size = spriteSize(id);
    this.shadow(x, y, size, 0.25 * (o.alpha ?? 1));
    this.drawImg(img, x, y + (o.bob ?? 0) / TILE + 0.12, size, size, faceRight, o.alpha ?? 1, o.squash ?? 1);
  }

  private drawTrainer(t: number) {
    const fit = mySprite();
    const tr = this.trainer;
    if (sheetInfo(fit)) {
      const now = performance.now();
      this.trainerAnim.face = FACING[tr.dir];
      if (tr.moving) this.trainerAnim.moved = now;
      this.trainerAnim.atk = tr.throwAt - ATK_MS;
      this.drawSheet(fit, false, tr.x, tr.y + 0.03, this.trainerAnim, now, {});
      return;
    }
    const id = `${fit}-${tr.dir}-0`;
    if (has(id)) { const img = sprite(id); if (this.ready(img)) { this.shadow(tr.x, tr.y + 0.1, 20); this.drawImg(img, tr.x, tr.y + 0.15, 32, 32); } }
  }

  /** The trainer walks ~1.2 tiles behind the lead along the lead's own trail, as on the hunt view. */
  private updateTrainer(lp: { x: number; y: number } | null, dtMs: number) {
    if (!lp) return;
    const trail = this.trail, tr = this.trainer;
    const last = trail[trail.length - 1];
    if (!last || Math.hypot(last.x - lp.x, last.y - lp.y) > 0.08) trail.push({ x: lp.x, y: lp.y });
    if (trail.length > 400) trail.splice(0, trail.length - 400);
    let want = 1.2, target = trail[0] ?? lp;
    for (let i = trail.length - 1; i > 0; i--) {
      const seg = Math.hypot(trail[i].x - trail[i - 1].x, trail[i].y - trail[i - 1].y);
      if (seg >= want) { const k = want / seg; target = { x: lerp(trail[i].x, trail[i - 1].x, k), y: lerp(trail[i].y, trail[i - 1].y, k) }; break; }
      want -= seg;
      target = trail[i - 1];
    }
    if (!tr.x && !tr.y) { tr.x = target.x; tr.y = target.y + 0.6; }
    const dx = target.x - tr.x, dy = target.y - tr.y, d = Math.hypot(dx, dy);
    const speed = (CONFIG.walkTilesPerSec * 1.15 * dtMs) / 1000;
    if (d > 0.05) {
      const step = Math.min(d, speed);
      tr.x += (dx / d) * step; tr.y += (dy / d) * step;
      tr.moving = d > 0.12;
      tr.dir = holdDir(tr.dir, steer(tr.head, dx, dy, dtMs).vx, tr.head.vy);
    } else {
      tr.moving = false;
      const ldx = lp.x - tr.x, ldy = lp.y - tr.y;
      if (Math.hypot(ldx, ldy) > 0.3 && performance.now() - tr.throwAt > 600) tr.dir = holdDir(tr.dir, ldx, ldy);
    }
  }

  // ---------------- overlays ----------------
  private hpBar(x: number, y: number, frac: number, wide: number) {
    const g = this.g, d = this.dpr;
    const w = Math.round(wide * d), h = Math.max(2, Math.round(2.5 * d));
    const px = Math.round(x - w / 2), py = Math.round(y);
    g.fillStyle = 'rgba(16,12,8,.85)'; g.fillRect(px - d, py - d, w + 2 * d, h + 2 * d);
    g.fillStyle = frac > 0.5 ? '#79b04a' : frac > 0.2 ? '#f0c45a' : '#e05a5a';
    g.fillRect(px, py, Math.round(w * Math.max(0, Math.min(1, frac))), h);
  }

  private text(s: string, x: number, y: number, color: string, px: number) {
    const g = this.g, d = this.dpr;
    g.font = `700 ${Math.round(px * d)}px Inter, system-ui, sans-serif`;
    g.textAlign = 'center'; g.lineJoin = 'round';
    g.lineWidth = Math.max(2, 3 * d); g.strokeStyle = 'rgba(20,13,8,.9)'; g.strokeText(s, x, y);
    g.fillStyle = color; g.fillText(s, x, y);
  }

  private catchRing(cx: number, cy: number, left: number, aura: boolean) {
    const g = this.g, r = Math.max(4 * this.dpr, 4.5 * this.K);
    g.strokeStyle = 'rgba(16,19,31,.7)'; g.lineWidth = 2.5 * this.dpr;
    g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.stroke();
    g.strokeStyle = aura ? '#f0c45a' : '#7fc8e8'; g.lineWidth = 2 * this.dpr;
    g.beginPath(); g.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * left); g.stroke();
  }

  private auraSparkle(x: number, y: number, t: number) {
    const g = this.g, K = this.K;
    for (let j = 0; j < 4; j++) {
      const a = t / 500 + j * 1.57;
      const px = this.sx(x) + Math.cos(a) * 11 * K, py = this.sy(y) - 12 * K + Math.sin(a) * 7 * K;
      const s = Math.max(1, Math.round(K));
      g.fillStyle = j % 2 ? '#f0c45a' : '#f1dcff';
      g.fillRect(Math.round(px - s / 2), Math.round(py - 1.5 * s), s, 3 * s);
      g.fillRect(Math.round(px - 1.5 * s), Math.round(py - s / 2), 3 * s, s);
    }
  }

  // ---------------- frame ----------------
  /** Draws one frame. `t` is the window's animation-frame time. */
  draw(t: number) {
    const g = this.g, f = S.field;
    const W = this.canvas.width, H = this.canvas.height;
    const dtMs = Math.min(100, t - (this.lastT || t));
    this.lastT = t;
    g.imageSmoothingEnabled = false;
    g.fillStyle = '#120d09';
    g.fillRect(0, 0, W, H);
    if (!f) return;
    if (f.ground !== this.ground) { this.ground = f.ground; this.reset(); }
    // Wilds come and go all day: forget the animation state of the ones that left.
    if (t - this.pruned > 10_000) {
      this.pruned = t;
      const here = new Set(f.wilds.map((w) => `w${w.uid}`));
      for (const k of this.anims.keys()) if (k !== 'lead' && !here.has(k)) this.anims.delete(k);
    }
    const { m, c: bg } = this.terrainFor(f.ground);
    const K = (this.K = sceneScale(W, H, this.shape, this.zoom));
    const alpha = Math.min(1, (performance.now() - S.fieldAt) / CONFIG.tickMs);
    const now = serverNow();
    const lp = this.leadPos(alpha);

    // Camera: the lead in the middle of the part of the window the HUD leaves open, never past the map's edge.
    const d = this.dpr, ins = this.insets;
    const viewW = W / K, viewH = H / K;
    const cx = (ins.l * d + (W - (ins.l + ins.r) * d) / 2) / K, cy = (ins.t * d + (H - (ins.t + ins.b) * d) / 2) / K;
    const focus = lp ?? m.start;
    const wantX = focus.x * TILE - cx, wantY = focus.y * TILE - cy + 6;
    const clampX = (v: number) => (viewW >= m.w * TILE ? (m.w * TILE - viewW) / 2 : Math.max(0, Math.min(m.w * TILE - viewW, v)));
    const clampY = (v: number) => (viewH >= m.h * TILE ? (m.h * TILE - viewH) / 2 : Math.max(0, Math.min(m.h * TILE - viewH, v)));
    if (!this.cam.ok) this.cam = { x: clampX(wantX), y: clampY(wantY), ok: true };
    const ease = 1 - Math.pow(0.004, dtMs / 1000);
    this.cam.x = clampX(lerp(this.cam.x, wantX, ease));
    this.cam.y = clampY(lerp(this.cam.y, wantY, ease));
    this.cam.x = Math.round(this.cam.x * K) / K; this.cam.y = Math.round(this.cam.y * K) / K;
    const cam = this.cam;

    g.drawImage(bg, cam.x, cam.y, viewW, viewH, 0, 0, W, H);
    this.updateTrainer(lp, dtMs);

    const inView = (x: number, y: number, pad = 3) => x * TILE > cam.x - pad * TILE && x * TILE < cam.x + viewW + pad * TILE && y * TILE > cam.y - pad * TILE && y * TILE < cam.y + viewH + pad * TILE;
    const items: { y: number; draw: () => void }[] = [];
    const over: (() => void)[] = [];

    for (const p of m.props) {
      if (!p.block || !inView(p.x, p.y) || !has(p.id)) continue;
      const img = sprite(p.id);
      if (!this.ready(img)) continue;
      const a = artInfo(p.id);
      if (a) {
        const fr = a.frames > 1 ? Math.floor(t / 320 + p.x * 3.7 + p.y * 1.3) % a.frames : 0;
        items.push({ y: p.y + 0.3, draw: () => g.drawImage(img, fr * a.w, 0, a.w, a.h, Math.round(this.sx(p.x) - (a.w / 2) * K), Math.round(this.sy(p.y + 0.42) - (a.h - a.foot) * K), Math.round(a.w * K), Math.round(a.h * K)) });
        continue;
      }
      const sz = spriteSize(p.id);
      items.push({ y: p.y + 0.3, draw: () => { this.shadow(p.x, p.y + 0.35, sz * 0.9, 0.18); this.drawImg(img, p.x, p.y + 0.42, sz, sz); } });
    }

    // The wild being fought: the nearest standing one within reach of the lead.
    let target: WildDTO | undefined;
    if (lp) {
      let best = 1.5;
      for (const w of f.wilds) { if (w.downUntil) continue; const p = this.wildPos(w, alpha); const dd = Math.hypot(p.x - lp.x, p.y - lp.y); if (dd < best) { best = dd; target = w; } }
    }

    for (const w of f.wilds) {
      const p = this.wildPos(w, alpha);
      if (!inView(p.x, p.y)) continue;
      items.push({ y: p.y, draw: () => {
        if (w.downUntil) {
          this.drawCreature(w.species, w.aura, p.x, p.y, false, { alpha: 0.7, squash: 0.55, key: `w${w.uid}`, t });
          over.push(() => {
            const left = Math.max(0, (w.downUntil! - now) / CONFIG.catchWindowMs);
            const rx = this.sx(p.x), ry = this.sy(p.y) - 13 * K;
            if (!w.throwing) this.catchRing(rx, ry, left, w.aura);
          });
          if (w.throwing) {
            const thrown = this.fx.find((e) => e.k === 'throw' && e.uid === w.uid) as Extract<Fx, { k: 'throw' }> | undefined;
            if (!thrown || performance.now() - thrown.t0 >= 450) {
              const fr = Math.floor(t / 120) % 4;
              const id = has(`prop-seal-spin-${fr}`) ? `prop-seal-spin-${fr}` : 'icon-rune-seal';
              const img = sprite(id);
              if (this.ready(img)) over.push(() => this.drawImg(img, p.x + Math.sin(t / 70) * 1.5 / TILE, p.y - 0.05, 16, 16));
            }
          }
        } else {
          const fighting = w === target;
          const bob = Math.abs(Math.sin(t / 220 + w.uid)) * -1.5;
          const face: Facing | undefined = fighting && lp ? (lp.x > p.x ? 'right' : 'left') : undefined;
          this.drawCreature(w.species, w.aura, p.x, p.y, lp ? lp.x > p.x : w.uid % 2 === 0, { bob, key: `w${w.uid}`, face, t });
          if (fighting || w.aura || w.hp < w.maxHp) {
            const top = this.sy(p.y) - Math.round(this.height(w.species) * K) - 3 * d;
            over.push(() => {
              if (fighting || w.hp < w.maxHp) this.hpBar(this.sx(p.x), top - 2 * d, w.hp / w.maxHp, 22);
              if (w.aura) this.text('Aura', this.sx(p.x), top - 7 * d, '#f0c45a', 10);
            });
          }
          if (w.aura) over.push(() => this.auraSparkle(p.x, p.y, t));
        }
      } });
    }

    if (lp && f.lead) {
      const tp = target ? this.wildPos(target, alpha) : null;
      const face = tp ? tp.x > lp.x : this.lastLeadX < lp.x - 0.001 ? true : this.lastLeadX > lp.x + 0.001 ? false : this.leadFace;
      this.leadFace = face; this.lastLeadX = lp.x;
      const c = S.creatures.get(f.lead.id);
      const lead = f.lead;
      if (c) items.push({ y: lp.y + 0.01, draw: () => {
        this.drawCreature(myArt(c.species, c.rolls.aura), c.rolls.aura, lp.x, lp.y, face, { bob: Math.abs(Math.sin(t / 180)) * -1, key: 'lead', face: tp ? (face ? 'right' : 'left') : undefined, t });
        over.push(() => this.hpBar(this.sx(lp.x), this.sy(lp.y) + 3 * K, lead.hp / lead.maxHp, 24));
        if (c.rolls.aura) over.push(() => this.auraSparkle(lp.x, lp.y, t));
      } });
    }
    items.push({ y: this.trainer.y + 0.02, draw: () => this.drawTrainer(t) });
    items.sort((a, b) => a.y - b.y).forEach((i) => i.draw());
    over.forEach((o) => o());
    this.drawFx(f.wilds);

    // Resting or no team: the scene dims; the window's veil says why.
    if (f.restingUntil > now || !f.lead) { g.fillStyle = 'rgba(12,8,5,.5)'; g.fillRect(0, 0, W, H); }
  }

  // ---------------- effects ----------------
  private drawFx(wilds: WildDTO[]) {
    const g = this.g, K = this.K, d = this.dpr, pnow = performance.now();
    for (let i = this.fx.length - 1; i >= 0; i--) {
      const e = this.fx[i];
      const age = pnow - e.t0;
      if (age < 0) continue;
      if (e.k === 'text') {
        const life = 1000;
        if (age > life) { this.fx.splice(i, 1); continue; }
        g.globalAlpha = age < life * 0.6 ? 1 : 1 - (age - life * 0.6) / (life * 0.4);
        const rise = this.quiet ? 0 : (age / 28) * d;
        this.text(e.text, this.sx(e.x), this.sy(e.y) - 20 * K - rise, e.color, e.px);
        g.globalAlpha = 1;
      } else if (e.k === 'impact') {
        if (!this.drawImpact(e, age)) this.fx.splice(i, 1);
      } else if (e.k === 'burst') {
        if (age > 700) { this.fx.splice(i, 1); continue; }
        const s = Math.max(1, Math.round(K * 1.5));
        g.fillStyle = e.color; g.globalAlpha = 1 - age / 700;
        for (let j = 0; j < e.n; j++) {
          const a = (j / e.n) * Math.PI * 2, r = (4 + age / 22) * K;
          g.fillRect(Math.round(this.sx(e.x) + Math.cos(a) * r - s / 2), Math.round(this.sy(e.y) - 8 * K + Math.sin(a) * r - s / 2), s, s);
        }
        g.globalAlpha = 1;
      } else if (e.k === 'throw') {
        if (age > 450) { if (age > 2000) this.fx.splice(i, 1); continue; }
        const w = wilds.find((x) => x.uid === e.uid);
        if (!w) { this.fx.splice(i, 1); continue; }
        const k = age / 450;
        const x = lerp(e.fx, w.x, k), y = lerp(e.fy, w.y, k) - Math.sin(k * Math.PI) * 1.2;
        const fr = Math.floor(age / 60) % 4;
        const img = sprite(has(`prop-seal-spin-${fr}`) ? `prop-seal-spin-${fr}` : 'icon-rune-seal');
        if (this.ready(img)) this.drawImg(img, x, y, 16, 16);
      }
    }
  }

  private rnd(seed: number, i: number, k: number) { const v = Math.sin(seed * 12.9898 + i * 78.233 + k * 37.719) * 43758.5453; return v - Math.floor(v); }

  /** n of the element's particle sprites thrown out from (cx, cy), moving the way the element moves. */
  private particles(el: Element, seed: number, cx: number, cy: number, t: number, n: number, reach: number) {
    const g = this.g, K = this.K;
    const pts: [number, number, boolean][] = [];
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + this.rnd(seed, i, 1) * 0.9;
      const sp = reach * (0.55 + this.rnd(seed, i, 2) * 0.6) * K;
      let dx = Math.cos(a) * sp * t, dy = Math.sin(a) * sp * t * 0.7;
      switch (el) {
        case 'flame': dy -= t * 12 * K; dx *= 0.6; break;
        case 'tide': dy += (t * t * 22 - t * 8) * K; break;
        case 'grove': dx += Math.sin(t * 7 + i) * 3 * K; dy += t * 7 * K; break;
        case 'stone': dy += (t * t * 28 - t * 6) * K; break;
        case 'frost': dx *= 0.7; dy = dy * 0.7 + t * 4 * K; break;
        case 'shade': dx = Math.cos(a) * sp * (1 - t); dy = Math.sin(a) * sp * (1 - t) * 0.7; break;
        case 'light': dx *= 1.3; dy *= 1.3; break;
        default: break;
      }
      pts.push([cx + dx, cy + dy, this.rnd(seed, i, 3) > 0.55 && t < 0.7]);
    }
    for (const [x, y, big] of pts) stamp(g, particleSprite(el, big, 'line'), x, y, K);
    for (const [x, y, big] of pts) stamp(g, particleSprite(el, big, 'fill'), x, y, K);
  }

  private ring(el: Element, cx: number, cy: number, r: number, n: number, color?: string) {
    const g = this.g, K = this.K, ramp = EL_RAMP[el], s = Math.max(1, Math.round(K * 1.6));
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      g.fillStyle = color ?? (i % 2 ? ramp[2] : ramp[4]);
      g.fillRect(Math.round(cx + Math.cos(a) * r * K - s / 2), Math.round(cy + Math.sin(a) * r * K * 0.55 - s / 2), s, s);
    }
  }

  /** One blow's impact; false once it has finished. A projectile flies first; a Signature drops a pillar. */
  private drawImpact(e: Extract<Fx, { k: 'impact' }>, age: number): boolean {
    const dur = IMPACT_MS[e.kind];
    if (age > dur) return false;
    const g = this.g, K = this.K, ramp = EL_RAMP[e.el];
    const cx = this.sx(e.x), cy = this.sy(e.y) - 10 * K;
    const flies = e.kind === 'projectile' || e.kind === 'beam';
    const sig = e.kind === 'signature';
    const land = flies ? FLIGHT_MS : sig ? 200 : 0;
    if (age < land) {
      if (flies) {
        const k = age / land, ox = this.sx(e.fx), oy = this.sy(e.fy) - 10 * K;
        stamp(g, particleSprite(e.el, true), lerp(ox, cx, k), lerp(oy, cy, k) - Math.sin(k * Math.PI) * 5 * K, K);
      } else {
        g.globalAlpha = 0.85;
        if (e.awk) { g.fillStyle = GILT[2]; g.fillRect(Math.round(cx - 8 * K), 0, Math.round(16 * K), Math.round(cy * (age / land))); }
        g.fillStyle = ramp[4]; g.fillRect(Math.round(cx - 5 * K), 0, Math.round(10 * K), Math.round(cy * (age / land)));
        g.globalAlpha = 1;
      }
      return true;
    }
    const t = (age - land) / (dur - land);
    if (t < 0.3) {
      const k = t / 0.3;
      g.globalAlpha = k < 0.67 ? 1 : (1 - k) * 3;
      stamp(g, sparkSprite(e.el, Math.floor(k * 3)), cx, cy, K);
    }
    g.globalAlpha = 1 - t;
    if (e.kind === 'wave') {
      const reach = WAVE_RADIUS * TILE;
      this.ring(e.el, cx, cy + 6 * K, 4 + Math.min(1, t * 1.25) * reach, 26);
      this.particles(e.el, e.seed, cx, cy, t, 10, reach * 0.8);
    } else if (sig) {
      if (t < 0.25) { g.globalAlpha = (1 - t / 0.25) * 0.8; g.fillStyle = e.awk ? GILT[3] : ramp[4]; g.fillRect(Math.round(cx - 5 * K * (1 - t * 4)), 0, Math.round(10 * K * (1 - t * 4)), Math.round(cy + 6 * K)); g.globalAlpha = 1 - t; }
      this.ring(e.el, cx, cy + 6 * K, 8 + t * 34, 40);
      if (e.awk) this.ring(e.el, cx, cy + 6 * K, 12 + t * 42, 48, GILT[2]);
      this.particles(e.el, e.seed, cx, cy, t, 18, 30);
    } else {
      this.particles(e.el, e.seed, cx, cy, t, e.kind === 'burst' ? 10 : 6, e.kind === 'burst' ? 18 : 12);
    }
    g.globalAlpha = 1;
    return true;
  }

  /** Hunt events: swings and flashes, one impact per blow, the coins a kill pays, seals thrown, catches. */
  events(evs: GameEvent[]) {
    const t0 = performance.now();
    const add = (e: Fx) => { this.fx.push(e); if (this.fx.length > 120) this.fx.splice(0, this.fx.length - 120); };
    for (const e of evs) {
      if (e.k === 'hit') {
        const atk = this.anims.get(e.by === 'lead' ? 'lead' : `w${e.uid}`), tgt = this.anims.get(e.by === 'lead' ? `w${e.uid}` : 'lead');
        if (atk) atk.atk = t0;
        if (tgt) tgt.hit = t0 + 2 * ATK_MS;
        if (e.by !== 'lead') continue;
        const p = this.posOf(e.uid), mv = MOVE[e.mv];
        if (!p || !mv) continue;
        const sig = mv.slot === 'signature' || mv.slot === 'awakened';
        // Damage shows only when it is the news: a Signature or a critical blow.
        if (!e.splash && (sig || e.crit)) add({ k: 'text', x: p.x, y: p.y, text: `${dmgText(e.dmg)}${e.crit ? '!' : ''}`, color: sig ? '#ffe2a0' : '#ffffff', t0, px: sig ? 15 : 12 });
        if (this.quiet) continue;
        const lead = S.field?.lead;
        add({ k: 'impact', kind: e.splash ? 'jab' : mv.anim.kind, el: mv.element, x: p.x, y: p.y, fx: lead?.x ?? p.x, fy: lead?.y ?? p.y, t0: t0 + (e.splash ? 120 : 0), seed: (t0 % 1000) + e.uid, awk: mv.slot === 'awakened' });
      } else if (e.k === 'down') {
        const p = this.posOf(e.uid);
        if (p) add({ k: 'text', x: p.x, y: p.y - 0.3, text: `+${dmgText(e.coins)}`, color: '#f0c45a', t0, px: 11 });
      } else if (e.k === 'throw') {
        add({ k: 'throw', uid: e.uid, fx: this.trainer.x, fy: this.trainer.y - 0.6, t0 });
        const p = this.posOf(e.uid);
        if (p && !this.trainer.moving) this.trainer.dir = dirTo(p.x - this.trainer.x, p.y - this.trainer.y);
        this.trainer.throwAt = t0;
      } else if (e.k === 'catch') {
        const p = this.posOf(e.uid);
        const aura = e.creature.rolls.aura;
        if (p && !this.quiet) add({ k: 'burst', x: p.x, y: p.y, color: aura ? '#f0c45a' : '#7fc8e8', t0, n: aura ? 20 : 10 });
      } else if (e.k === 'miss') {
        const p = this.posOf(e.uid);
        if (p) add({ k: 'text', x: p.x, y: p.y, text: 'broke free', color: '#e8ddc8', t0, px: 10 });
      }
    }
  }
}
